import { useCallback, useEffect, useState } from 'react';
import { Button, Grid, TextField, Typography } from '@material-ui/core';
import { Content, Header, InfoCard, Page, Progress, ResponseErrorPanel, Table } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';

type Snapshot = {
  cluster: string;
  observedAt: string;
  nodes: { name: string; ready: boolean; version: string }[];
  namespaces: { name: string; phase: string }[];
  deployments: { name: string; namespace: string; desired: number; ready: number; available: number; updated: number; healthy: boolean }[];
  pods: { name: string; namespace: string; phase: string; ready: number; containers: number; restarts: number; reason: string }[];
};

export function ClusterPage() {
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [snapshot, setSnapshot] = useState<Snapshot>();
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(false);
  const [namespace, setNamespace] = useState('');
  const refresh = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const url = await discovery.getBaseUrl('cluster-status');
      const response = await fetchApi.fetch(`${url}/`, { signal });
      if (!response.ok) throw new Error(`Cluster status is unavailable (HTTP ${response.status}).`);
      const data = await response.json();
      if (!signal?.aborted) { setSnapshot(data); setError(undefined); }
    } catch (cause) {
      if (!signal?.aborted) setError(cause as Error);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [discovery, fetchApi]);
  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    const timer = window.setInterval(() => { void refresh(controller.signal); }, 30_000);
    return () => { controller.abort(); window.clearInterval(timer); };
  }, [refresh]);

  const options = { paging: true, pageSize: 20, search: true, padding: 'dense' as const };
  const deployments = snapshot?.deployments.filter(d => !namespace || d.namespace === namespace) ?? [];
  const pods = snapshot?.pods.filter(p => !namespace || p.namespace === namespace) ?? [];
  const unhealthy = snapshot?.deployments.filter(d => !d.healthy).length ?? 0;
  return <Page themeId="tool">
    <Header title="Current cluster" subtitle={snapshot?.cluster} />
    <Content>
      {loading && <Progress />}
      {error && <ResponseErrorPanel error={error} />}
      {snapshot && <>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}><InfoCard title="Nodes"><Typography>{snapshot.nodes.filter(n => n.ready).length} / {snapshot.nodes.length} ready</Typography></InfoCard></Grid>
          <Grid item xs={12} md={4}><InfoCard title="Deployments"><Typography>{unhealthy ? `${unhealthy} need attention` : 'All deployments ready'}</Typography></InfoCard></Grid>
          <Grid item xs={12} md={4}><InfoCard title="Namespaces"><Typography>{snapshot.namespaces.length} namespaces</Typography></InfoCard></Grid>
          <Grid item xs={12}>
            <Typography color={error ? 'error' : 'textSecondary'}>
              {error ? 'Showing the last successful snapshot. ' : ''}Observed {new Date(snapshot.observedAt).toLocaleString()}. Refreshes every 30 seconds.
            </Typography>
            <Button onClick={() => { void refresh(); }} disabled={loading}>Refresh</Button>
            <TextField select label="Namespace" value={namespace} onChange={event => setNamespace(event.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
              <option value="">All namespaces</option>
              {snapshot.namespaces.map(n => <option key={n.name} value={n.name}>{n.name}</option>)}
            </TextField>
          </Grid>
          <Grid item xs={12}><Table title="Deployments" options={options} columns={[
            { title: 'Name', field: 'name' }, { title: 'Namespace', field: 'namespace' },
            { title: 'Ready', render: row => `${row.ready}/${row.desired}` },
            { title: 'Updated', field: 'updated' },
            { title: 'Status', render: row => row.healthy ? 'Ready' : 'Needs attention' },
          ]} data={deployments} /></Grid>
          <Grid item xs={12}><Table title="Pods" options={options} columns={[
            { title: 'Name', field: 'name' }, { title: 'Namespace', field: 'namespace' },
            { title: 'Phase', field: 'phase' }, { title: 'Ready', render: row => `${row.ready}/${row.containers}` },
            { title: 'Restarts', field: 'restarts' }, { title: 'Reason', field: 'reason' },
          ]} data={pods} /></Grid>
          <Grid item xs={12}><Table title="Nodes" options={{ ...options, paging: false }} columns={[
            { title: 'Name', field: 'name' }, { title: 'Status', render: row => row.ready ? 'Ready' : 'Not ready' },
            { title: 'Kubernetes version', field: 'version' },
          ]} data={snapshot.nodes} /></Grid>
        </Grid>
      </>}
    </Content>
  </Page>;
}
